import React from "react";
import { Link, graphql } from "gatsby";
import styled from 'styled-components';
import { MDXRenderer } from "gatsby-plugin-mdx";
import Img from 'gatsby-image';
// import { GatsbyImage } from "gatsby-plugin-image"


import {AnotherLayout}  from "../components/Layout";
import Seo from "../components/seo";
import { rhythm } from "../utils/typography";
import BreadCrumb from '../components/BreadCrumbs.js';
import ZigZag from "../components/ZigZag";
import Toc from '../components/Toc';
import SharePost from '../components/SharePost.js';
import BioBrief from "../components/BioBrief";
import GetInTouch from "../components/GetInTouch";

const LinkDiv = styled.div`
    display:inline-block;
    border-radius:8px;
    padding:${rhythm(0.25)} ${rhythm(0.75)};
    >p{
      margin:0;
    }
    :hover{
      background:var(--color-foreground);
    }
`;

const HeaderData = styled.div`
  border-right: 3px solid var(--color-gray3);
  border-radius:1.5px;
  padding: ${rhythm(0.5)};
  flex:1 0 300px;
  text-align:right;
  order:1;
`;


const HeaderImage = styled(Img)`
  border-radius: 30px;
  flex:2 0 300px;
  max-height: 60vh;
  order:2;
`;

const Jumbotron = styled.section`
  margin:${rhythm(2)} 0;
  display:flex;
  flex-wrap:wrap;
  align-items:center;
  > div{
    margin:${rhythm(0.35)};
  }
  @media(max-width: 750px) {
    margin:${rhythm(0.5)} 0;
    ${HeaderImage} {
      order: 1;
      flex: 0 0 100%;
      margin:0;
      margin-top:${rhythm(1.5)};
    }
    ${HeaderData}{
      order: 2;
      flex: 0 0 100%;
      border:none;
      text-align:left;
      margin:0;
      margin-top:${rhythm(1)};
    }
  }
  @media(max-width: 650px) {
    ${HeaderData}{
      padding:0;
      margin-top:${rhythm(1)};
    }
  }
`;

const ContentDiv = styled.div`
  width: 100%;
  margin: ${rhythm(1.5)} 0;
  padding:0 ${rhythm(1.5)};
  @media(max-width: 650px) {
    padding:0;
  }
`;

const MainSec = styled.div`
  width:100%;
  display: flex;
  gap: 5ch;
  flex-wrap:wrap;
`;


const ArticleSideSec = styled.div`
  flex: 4 1 200px;
  width:10%;
  @media only screen and (max-width: 880px) {
    display:none;
  }
  `;

const ArticleMainSec = styled.main`
  flex: 1 6 400px;
  width:70%;
`;

const BlogPostTemplate = ({ data, pageContext, location }) => {
  const post = data.mdx;
  const { previous, next } = pageContext;
  const { title, caption, description, category, toc, date } = post.frontmatter;
  const featuredImage = post.frontmatter.featuredImage.childImageSharp.fluid;
  return (
    <AnotherLayout>
      <Seo
        title={title}
        article={true}
        description={description || post.excerpt}
        pathname={location.pathname}
        image={featuredImage}
      />
      {toc ? (
        <>
          <Jumbotron>
            <HeaderData>
              <BreadCrumb list={['Home', 'Categories', category]} />
              <h1 style={{ margin: '6px 0' }}>{title}</h1>
              <p className="caption">{caption}</p>
              <ZigZag />
            </HeaderData>
            <HeaderImage
              fluid={featuredImage}
              objectFit="cover"
              objectPosition="50% 50%"
              fadeIn={true}
              alt={title + " header image"}
            />
          </Jumbotron>
          <BioBrief publishedDate={date} timeToRead={post.timeToRead}/>
              
          <MainSec>
            <ArticleMainSec>
              <MDXRenderer>
                {post.body}
              </MDXRenderer>
              <GetInTouch/>
            </ArticleMainSec>
            <ArticleSideSec>
              <div style={{
                  marginBottom: rhythm(0.5),
                  position: 'sticky',
                  top: '20px'
                }}>
                  <Toc headings={post.headings} toc={toc} />
                  <br />
                  {title && post?.fields?.slug && <SharePost
                    path={location.href}
                    title={title}
                    description={description}
                  />}
              </div>
            </ArticleSideSec>
          </MainSec>
        </>
      ) : (
        <ContentDiv>
          <div style={{
            textAlign: 'center',
            width: '100%',
            margin: `${rhythm(2.5)} 0`,
          }}>
            <h1>{title}</h1>
            <small style={{ display: 'block' }}>{caption}</small>
          </div>
          <div style={{
            width: '100%'
          }}>
            <HeaderImage
              fluid={featuredImage}
              objectFit="cover"
              objectPosition="50% 50%"
              fadeIn={true}
              alt={title + " header image"}
            />
            <BioBrief publishedDate={date} timeToRead={post.timeToRead}/>
            <MDXRenderer>{post.body}</MDXRenderer>
            <br/>
            <hr/>
            {title && post?.fields?.slug && <SharePost
              path={post.fields.slug}
              title={title}
              description={description}
              />}
              <GetInTouch/>
          <div style={{ textAlign: 'center' }}>
            <ZigZag />
          </div>
          </div>
        </ContentDiv>
      )}

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        marginTop: rhythm(4)
      }}>
        <div >
          {previous && (
            <Link to={previous.fields.slug} rel="prev">
              <LinkDiv style={{ textAlign: "right" }}>
                <p style={{ color: "var(--color-headingFont)" }}>Previous</p>
                <p style={{ color: `var(--color-secondary)` }}>{previous.frontmatter.title}</p>
              </LinkDiv>
            </Link>
          )}
        </div>
        <div>
          {next && (
            <Link to={next.fields.slug} rel="next">
              <LinkDiv style={{ textAlign: "left" }}>
                <p style={{ color: "var(--color-headingFont)" }}>Next</p>
                <p style={{ color: `var(--color-secondary)` }}>{next.frontmatter.title}</p>
              </LinkDiv>
            </Link>
          )}
        </div>
      </div>
    </AnotherLayout>
  )
}



export const pageQuery = graphql`
  query BlogPostBySlug($slug: String!) {
    mdx(fields: { slug: { eq: $slug } }) {
      id
      excerpt(pruneLength: 160)
      body
      timeToRead
      headings(depth: h2) {
        value
      }
      fields {
        slug
      }
      frontmatter {
        title
        caption
        category
        date(formatString: "MMMM DD, YYYY")
        description
        toc
        featuredImage{
          childImageSharp {
            fluid(maxHeight: 450, quality: 75) {
              ...GatsbyImageSharpFluid
            }
          }
        }
      }
    }
  }
`

export default BlogPostTemplate









// <MainSec>
//             <ArticleMainSec>
//               <div style={{ display: "flex", flexDirection: "column", alignItems:"stretch"}}>
//                 <MDXRenderer>
//                   {post.body}
//                 </MDXRenderer>
//                 <GetInTouch/>
//                 </div>
//               <div style={{ textAlign: 'center' }}>
//                 <ZigZag />
//               </div>
//             </ArticleMainSec>
//             <ArticleSideSec>
              // <div style={{
              //   marginBottom: rhythm(0.5),
              //   position: 'sticky',
              //   top: '20px'
              // }}>
              //   <TOC headings={post.headings} toc={toc} />
              //   <br />
              //   {title && post?.fields?.slug && <SharePost
              //     path={location.href}
              //     title={title}
              //     description={description}
              //   />}
              // </div>
//             </ArticleSideSec>
//           </MainSec>